export interface AggregatedItem {
  name: string;
  date: Date;
  fullValue: string;
}

export interface ExtractedDataEntry {
  value: string | null;
  description: string;
  last_updated: string;
}

/**
 * Create set of aggregated fields that exist in dataToCategoryMap
 */
export const createAggregatedFieldsSet = (
  dataToCategoryMap: { [key: string]: unknown },
  aggregatedFieldNames: readonly string[] | null | undefined
): Set<string> => {
  if (!aggregatedFieldNames || aggregatedFieldNames.length === 0) {
    return new Set<string>();
  }
  return new Set<string>(
    aggregatedFieldNames.filter((fieldName) => fieldName in dataToCategoryMap)
  );
};

/**
 * Parse list value (pipe or comma separated)
 */
export const parseListValue = (value: string): string[] => {
  if (!value || value === "[]") return [];
  // Try pipe first, then comma
  if (value.includes(" | ")) {
    return value.split(" | ").map((s) => s.trim());
  }
  return value.split(",").map((s) => s.trim());
};

/**
 * Extract the base name from a formatted value
 */
export const extractBaseName = (formattedValue: string): string => {
  const match = formattedValue.match(/^([^(]+)/);
  return match ? match[1].trim() : formattedValue.trim();
};

/**
 * Parse a date string, treating date-only strings (YYYY-MM-DD) as local dates
 * to avoid timezone conversion issues
 */
const parseDateString = (dateStr: string): Date | null => {
  const trimmed = dateStr.trim();
  
  const dateOnlyMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    const localDate = new Date(
      parseInt(year, 10),
      parseInt(month, 10) - 1,
      parseInt(day, 10)
    );
    return localDate;
  }
  
  const parsedDate = new Date(trimmed);
  if (!isNaN(parsedDate.getTime())) {
    return parsedDate;
  }
  
  return null;
};

/**
 * Extract date from formatted value string - checked for started: and recorded:
 */
export const extractDateFromFormattedValue = (
  formattedValue: string
): Date | null => {
  const startedMatch = formattedValue.match(/started:\s*([^;)]+)/i);
  if (startedMatch) {
    const parsedDate = parseDateString(startedMatch[1]);
    if (parsedDate) {
      return parsedDate;
    }
  }
  
  const recordedMatch = formattedValue.match(/recorded:\s*([^;)]+)/i);
  if (recordedMatch) {
    const parsedDate = parseDateString(recordedMatch[1]);
    if (parsedDate) {
      return parsedDate;
    }
  }
  
  return null;
};

/**
 * Aggregate entries for fields that need aggregation across conversation runs
 */
export const aggregateFieldData = (
  entries: ExtractedDataEntry[]
): AggregatedItem[] => {
  const allItems: AggregatedItem[] = [];

  entries.forEach((entry) => {
    if (!entry.value || entry.value === "[]") return;

    const entryDate = new Date(entry.last_updated);
    const parsedItems = parseListValue(entry.value);

    parsedItems.forEach((item) => {
      const date = extractDateFromFormattedValue(item) ?? entryDate;
      allItems.push({
        name: extractBaseName(item),
        date,
        fullValue: item,
      });
    });
  });

  allItems.sort((a, b) => b.date.getTime() - a.date.getTime());

  return allItems;
};
